/**
 * Strings that are stored, and strings that are read back.
 *
 * A plain string assigned into an Automerge document in this version becomes a
 * TEXT object: one op per character, kept forever. Measured on the project
 * document that drove an 8 GB machine out of application memory, 677,530 of its
 * 762,704 ops (88.8%) were per-character writes into 29,479 such objects, for
 * 0.9 MB of state. A 27-character document id cost 28 ops.
 *
 * An id, a path, a copied anchor: nobody types into half of one, so none of
 * them needs a text object. `imm` writes them as one op. Prose a person edits
 * together — a comment body, a document's content — stays text, because that
 * is what text is for.
 *
 * `str` is the other half, and is not optional. An `ImmutableString` is NOT a
 * JS string: `typeof v === 'string'` is false for it. A reader that tests that
 * way reads a field that is there as absent and hands back an empty one, with
 * no error anywhere. Every read of a stored string goes through `str`.
 */

import * as A from '@automerge/automerge/slim'

/**
 * A string field as it is STORED: a wrapper on the way out of a document, a
 * plain string on anything written before the conversion. Read it with `str`.
 */
export type StoredText = string | { toString(): string }

/** Store a string that is never co-edited: one op, not one per character. */
export function imm(s: unknown): A.ImmutableString {
  return new A.ImmutableString(String(s ?? ''))
}

/** Read a stored string, whether it is a wrapper, text, or a plain string. */
export function str(v: unknown): string {
  if (typeof v === 'string') return v
  if (v == null) return ''
  const inner = (v as { val?: unknown }).val
  return typeof inner === 'string' ? inner : ''
}

/**
 * Compare two stored strings by their characters.
 *
 * Two wrappers holding the same characters are different objects, so `===`
 * answers "changed" every time. A guard that uses it rewrites the row on every
 * pass, which costs more than the bug it was added to avoid.
 */
export function sameStr(a: unknown, b: unknown): boolean {
  return a === b || str(a) === str(b)
}

/**
 * The same value with every stored string read back as a plain string.
 *
 * FOR A PROJECTION, not for a reader. The point of storing a scalar is to save
 * ops in the DOCUMENT, not to make every reader wrapper-aware: a reader that
 * has to remember is a reader that will forget, and forgetting is silent here.
 * `a.presentation === 'layout'` and `a.handles?.includes(mime)` are both false
 * against a wrapper, with no error, so the app that edits a mime type stops
 * being found and a layout stops being a layout.
 *
 * So a collection that comes off the log is unwrapped ONCE, where it enters the
 * model, and everything downstream compares plain strings as it always did.
 * Cheap because it runs on the thin catalog entry a projection already walks,
 * never on prose: a body stays a text object and is not copied here.
 *
 * Duck-typed rather than `instanceof`, because the window and the daemon can
 * hold different automerge instances and a wrapper from one is not an instance
 * of the other's class.
 */
export function plainly<T>(value: T): T {
  return unwrapDeep(value, 0) as T
}

const MAX_DEPTH = 12

function isWrapper(v: object): boolean {
  if (v instanceof A.ImmutableString) return true
  const keys = Object.keys(v)
  return (
    keys.length === 1 &&
    keys[0] === 'val' &&
    typeof (v as { val?: unknown }).val === 'string'
  )
}

function unwrapDeep(v: unknown, depth: number): unknown {
  if (!v || typeof v !== 'object') return v
  if (isWrapper(v)) return String((v as { val?: unknown }).val ?? v)
  // A guard, not a limit anyone should reach: a catalog entry is a handful of
  // levels deep and a cycle off the log would otherwise hang the window.
  if (depth >= MAX_DEPTH) return v
  if (Array.isArray(v)) return v.map((x) => unwrapDeep(x, depth + 1))
  const out: Record<string, unknown> = {}
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    out[k] = unwrapDeep(x, depth + 1)
  }
  return out
}
